import { useWidgetSharedSettings } from '@widgets/messenger/compositions/useWidgetSharedSettings'
import { isMessengerCustomColorTheme, isMessengerPaletteColorTheme } from '@widgets/messenger/types'
import type { WidgetButtonColor, WidgetButtonCustomColor } from '@widgets/shared/types'
import { computed } from '@/composition/vue/compositionApi'

export const useWidgetTheme = () => {
  const sharedSettings = useWidgetSharedSettings()

  const getThemeClasses = (prefix: string) => {
    const theme = sharedSettings.value.theme
    if (!isMessengerPaletteColorTheme(theme)) {
      return {}
    }
    return [`${prefix}--${theme.color}`, `${prefix}--${theme.type}`]
  }

  const getThemePrimaryColorStyles = () => {
    const theme = sharedSettings.value.theme
    if (!isMessengerCustomColorTheme(theme)) {
      return {
        backgroundColor: undefined,
        color: undefined,
      }
    }
    return {
      backgroundColor: theme.primaryColor.background,
      color: theme.primaryColor.font,
    }
  }

  const getThemeBlockWithBackgroundProps = (classPrefix: string) => {
    return {
      style: getThemePrimaryColorStyles(),
      class: getThemeClasses(classPrefix),
    }
  }

  const getThemeIconProps = (classPrefix: string) => {
    return {
      style: {
        color: getThemePrimaryColorStyles().backgroundColor,
      },
      class: getThemeClasses(classPrefix),
    }
  }

  const getThemeButtonProps = (): { color?: WidgetButtonColor | WidgetButtonCustomColor } => {
    const theme = sharedSettings.value.theme
    if (isMessengerPaletteColorTheme(theme)) {
      return {
        color: theme.color,
      }
    }
    if (isMessengerCustomColorTheme(theme)) {
      return {
        color: {
          primary: theme.buttonColor.background,
          secondary: theme.buttonColor.font,
        },
      }
    }
    return {}
  }

  const getThemeTransparentButtonOnBackgroundProps = (): { color?: WidgetButtonColor | WidgetButtonCustomColor } => {
    const theme = sharedSettings.value.theme
    if (isMessengerPaletteColorTheme(theme)) {
      return {
        color: {
          primary: '#FFFFFF',
          secondary: '#FFFFFF',
        },
      }
    }
    if (isMessengerCustomColorTheme(theme)) {
      return {
        color: {
          primary: theme.primaryColor.font,
          secondary: theme.primaryColor.font,
        },
      }
    }
    return {}
  }

  const getThemeChatMessageLayoutProps = (classPrefix: string, direction: 'outcoming' | 'incomming') => {
    const theme = sharedSettings.value.theme
    if (isMessengerPaletteColorTheme(theme)) {
      return {
        class: direction === 'outcoming' ? `${classPrefix}--${theme.color}` : '',
      }
    }
    if (isMessengerCustomColorTheme(theme)) {
      return {
        class: direction === 'outcoming' ? `${classPrefix}--blue` : '',
      }
    }
    return {}
  }

  const themeButtonProps = computed(() => getThemeButtonProps())

  const themeTransparentButtonOnBackgroundProps = computed(() => getThemeTransparentButtonOnBackgroundProps())

  const backgroundContrastColor = computed(() => {
    const theme = sharedSettings.value.theme
    if (isMessengerCustomColorTheme(theme)) {
      return theme.primaryColor.font
    }
    return '#FFFFFF'
  })

  return {
    getThemeBlockWithBackgroundProps,
    getThemeIconProps,
    getThemeButtonProps,
    getThemeChatMessageLayoutProps,
    themeButtonProps,
    themeTransparentButtonOnBackgroundProps,
    backgroundContrastColor,
  }
}
